/* eslint-disable import/no-cycle */
import { compact, get, groupBy, keyBy, uniq } from 'lodash';
import {
  convertObjToCamelCase,
  convertObjToSnakeCase,
  convertPascalToSnake,
  retrieveDataMapper,
  retrieveObjectMapper,
} from 'utils';
import api from 'utils/ms-utils/ceb-utils/ApiServices';
import {
  DEFAULT_ERROR,
  DEFAULT_ITEMS_PER_PAGE,
  DEFAULT_PAGE,
  MAX_PER_PAGE,
  PENDING,
} from '../utils/constants';
import DatadogHandler from '../utils/datadog';
import {
  centreSerivce,
  MS_PHASE_3_ENDPOINTS,
  MS_PHASE_2B_CLASS_ACTIVITY_ENPOINT,
  CENTRE_SVC_ENDPOINT,
} from '../utils/msEndpointConstants';
import { getAddressesByCentreIds, getUserByIds } from './UserService';
import { retrieveListOrObject, retrieveSortForMS } from '../utils';
import {
  transformFindAllCentreForSchool,
  transformCentreDocuments,
  transformCentreActivityLogs,
} from '../utils/ms-utils/ms-phase-3/CentreUtils';
import {
  getBillableItems,
  getLinkpointAwardConfigsByCentreIds,
  getLinkpointAwardConfigDraftsByCentreIds,
} from './FinanceService';
import { getConfigByCategories } from './SchoolConfigService';

const fileName = 'src/services/CentreService.js';

const { CENTRE } = MS_PHASE_3_ENDPOINTS;
const { CENTRE: CENTRE_P2B } = MS_PHASE_2B_CLASS_ACTIVITY_ENPOINT;

export const msSuggestPrograms = async (_, reqData) => {
  try {
    const reqBody = {
      centre_id: reqData.centreID,
      level_id: reqData.levelID,
    };
    const centreSvc = centreSerivce();
    const response = await centreSvc.post(
      ...CENTRE.getSuggestPrograms(reqBody)
    );

    const programs = response?.data?.data || [];
    const data = {
      suggestProgram: {
        data:
          programs?.map(item => ({
            ...retrieveObjectMapper(item, ['ID:id', 'label']),
          })) || [],
        totalCount: response?.data?.totalCount || 0,
      },
    };
    return { success: true, data };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msSuggestPrograms ',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const getHolidayOfYearForCalendar = (reqBody = {}) => {
  return centreSerivce()
    .post(...CENTRE.getHolidayOfYearForCalendar(reqBody))
    .then(r => ({
      data: get(r, 'data.data', []),
      totalCount: get(r, 'data.totalCount', 0),
    }))
    .catch(() => ({ data: [], totalCount: 0 }));
};

export const getCentreLevelByCentreIds = (centreIds = []) => {
  if (centreIds?.length === 0) return {};
  return centreSerivce()
    .post(...CENTRE.getCentreLevelByCentreIds(centreIds))
    .then(r => retrieveListOrObject(r, false))
    .catch(() => ({}));
};

export const getNearbyPostalCodeByCentreIds = (centreIds = []) => {
  if (centreIds?.length === 0) return {};
  return centreSerivce()
    .post(...CENTRE.getNearbyPostalCodeByCentreIds(centreIds))
    .then(r => retrieveListOrObject(r, false))
    .catch(() => ({}));
};

export const getCentreLevelProgramByFilter = (filters = {}, params = {}) => {
  return centreSerivce()
    .post(...CENTRE.getCentreLevelProgramByFilter(filters, params))
    .then(r => retrieveListOrObject(r, false))
    .catch(() => ({}));
};

export const getCentreWorkplaceByCentreIds = (centreIds = []) => {
  if (centreIds?.length === 0) return {};
  return centreSerivce()
    .post(...CENTRE.getCentreWorkplaceByCentreIds(centreIds))
    .then(r => get(r, 'data.result', {}))
    .catch(() => ({}));
};

export const getCentreDraftWorkplaces = (draftIDs = []) => {
  if (draftIDs?.length === 0) return Promise.resolve({});
  return centreSerivce()
    .post(...CENTRE.getCentreDraftWorkplaces(draftIDs))
    .then(r => retrieveListOrObject(r, false))
    .catch(() => ({}));
};

export const getCentreDraftAddresses = (draftIDs = []) => {
  if (draftIDs?.length === 0) return Promise.resolve({});
  return centreSerivce()
    .post(...CENTRE.getCentreDraftAddresses(draftIDs))
    .then(r => retrieveListOrObject(r, false))
    .catch(() => ({}));
};

export const getCentreDraftLevels = (draftIDs = []) => {
  if (draftIDs?.length === 0) return Promise.resolve({});
  return centreSerivce()
    .post(...CENTRE.getCentreDraftLevels(draftIDs))
    .then(r => retrieveListOrObject(r, false))
    .catch(() => ({}));
};

export const getCentreDraftNearbyPostcodes = (draftIDs = []) => {
  if (draftIDs?.length === 0) return Promise.resolve({});
  return centreSerivce()
    .post(...CENTRE.getCentreDraftNearbyPostcodes(draftIDs))
    .then(r => retrieveListOrObject(r, false))
    .catch(() => ({}));
};

export const getCentreDraftLevelPrograms = (draftIDs = []) => {
  if (draftIDs?.length === 0) return Promise.resolve({});
  return centreSerivce()
    .post(...CENTRE.getCentreDraftLevelPrograms(draftIDs))
    .then(r => retrieveListOrObject(r, false))
    .catch(() => ({}));
};

export const msGetAllCentreForSchool = async (_, reqBody) => {
  try {
    const isFetchList = get(reqBody, 'fetchType') === 'LIST';
    const onlyIncludeAddresses = get(reqBody, 'onlyIncludeAddresses', false);
    const filter = {
      ...(get(reqBody, 'filter.ID') && {
        id: [{ eq: get(reqBody, 'filter.ID') }],
      }),
      ...(get(reqBody, 'filter.label') && {
        label: get(reqBody, 'filter.label') || [],
      }),
    };

    const resp = await centreSerivce()
      .post(
        ...CENTRE.getCentreByFilters(
          {
            includeClosedCentre: true,
            filter,
          },
          {
            page: get(reqBody, 'pagination.page', 1),
            perPage: get(reqBody, 'pagination.perPage', MAX_PER_PAGE),
            ...retrieveSortForMS(get(reqBody, 'pagination.sort', [])),
          }
        )
      )
      .then(r => ({
        data: get(r, 'data.data', []),
        totalCount: get(r, 'data.totalCount', 0),
      }))
      .catch(() => ({ data: [], totalCount: 0 }));

    const userIds = uniq(
      compact(get(resp, 'data', [])?.map(d => d.last_updated_by_fk_user))
    );
    const centreIds = uniq(compact((get(resp, 'data') || [])?.map(c => c.id)));

    const emptyPromise = Promise.resolve();
    const promiseResponses = await Promise.all([
      ...(isFetchList ? [getUserByIds(userIds)] : [emptyPromise]),
      ...(!isFetchList || onlyIncludeAddresses
        ? [getAddressesByCentreIds(centreIds)]
        : [emptyPromise]),
      ...(!isFetchList && !onlyIncludeAddresses
        ? [
            getCentreLevelByCentreIds(centreIds),
            getNearbyPostalCodeByCentreIds(centreIds),
            getCentreLevelProgramByFilter({ centreIds }),
            getCentreWorkplaceByCentreIds(centreIds),
            getBillableItems({
              filter: {
                centre_id: [{ eq: centreIds }],
                type: [{ eq: 'registration_fee' }],
              },
            }),
            getLinkpointAwardConfigsByCentreIds(centreIds),
          ]
        : Array.from({ length: 6 }, () => emptyPromise)),
    ]);

    return transformFindAllCentreForSchool(
      {
        isFetchList,
        onlyIncludeAddresses,
      },
      ...[resp, ...promiseResponses]
    );
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetAllCentreForSchool',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msFetchCentreDocuments = async (_, reqData) => {
  try {
    const {
      centreID,
      centreDocumentCategoriesIDs,
      page = DEFAULT_PAGE,
      perPage = DEFAULT_ITEMS_PER_PAGE,
      activeSort = '-createdAt',
    } = reqData;

    const centreSvc = centreSerivce();
    const response = await centreSvc.post(
      `${CENTRE_SVC_ENDPOINT.GET_CENTRE_DOCUMENTS}?${new URLSearchParams({
        page,
        perPage,
        sort: activeSort,
      })}`,
      {
        centre_id: centreID,
        centre_document_category_ids: centreDocumentCategoriesIDs,
      }
    );

    const documents = response?.data?.data || [];
    const totalCount = response?.data?.totalCount || 0;

    const userIds = uniq(compact(documents.map(doc => doc.fk_user)));

    let usersMap = {};
    if (userIds.length > 0) {
      const users = await getUserByIds(userIds);
      usersMap = (users || []).reduce((acc, user) => {
        acc[user.id] = user;
        return acc;
      }, {});
    }

    const data = {
      listAllCentreDocumentsByCategories: {
        data: transformCentreDocuments(documents, usersMap),
        totalCount,
      },
    };

    return { success: true, data };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msFetchCentreDocuments',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const getCentreByIds = (ids = []) => {
  if (!ids.length)
    return {
      data: [],
      totalCount: 0,
    };
  return api.centreService
    .post(...CENTRE_P2B.GET_CENTRE_BY_IDS(ids))
    .then(r => ({
      data: get(r, 'data', []),
      totalCount: get(r, 'totalCount', 0),
    }))
    .catch(() => ({
      data: [],
      totalCount: 0,
    }));
};

export const getProgramByIds = (ids = []) => {
  if (!ids.length) return [];
  return centreSerivce()
    .post(...CENTRE_P2B.GET_PROGRAMS_BY_IDS(ids))
    .then(retrieveListOrObject)
    .catch(() => []);
};

export const msFindAllCentreLevel = async (_, reqData) => {
  try {
    const { filter } = reqData;
    const centreSvc = centreSerivce();
    const response = await centreSvc.post(CENTRE.findAllCentreLevel, {
      centre_ids: filter?.fkCentre ? [filter?.fkCentre] : [],
    });

    const levels = response?.data?.data?.[filter?.fkCentre] || [];
    return {
      success: true,
      data: {
        findAllCentreLevel: {
          data:
            levels?.data?.map(item => ({
              level: convertObjToCamelCase(item),
            })) || [],
          totalCount: levels.totalCount || 0,
        },
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msSuggestPrograms ',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msFetchAllTeachers = async (_, reqData) => {
  try {
    const { ftsInput, fkSchool, IDCentre, isGetSeniorTeacher } = reqData;
    if (!ftsInput)
      return {
        success: true,
        data: { getTeacherListOnInput: { data: [], totalCount: 0 } },
      };

    const centreSvc = centreSerivce();
    const response = await centreSvc
      .post(CENTRE.getTeacherListOnInput, {
        fts_input: ftsInput,
        school_id: fkSchool,
        ...(IDCentre && { centre_id: IDCentre }),
        is_senior_teacher: false && isGetSeniorTeacher,
      })
      .catch(() => ({}));
    const classTeachers = get(response, 'data.data') || [];
    const teacherIds = uniq(compact(classTeachers?.map(t => t.fk_teacher)));
    const teachers = await getUserByIds(teacherIds);

    return {
      success: true,
      data: {
        getTeacherListOnInput: {
          data:
            classTeachers.map(item => ({
              user: retrieveDataMapper(teachers, item.fk_teacher, [
                'ID:id',
                'firstname',
                'lastname',
              ]),
            })) || [],
          totalCount: get(response, 'data.totalCount', 0),
        },
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msFetchAllTeachers ',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            error,
            'response.data.message',
            get(error, 'message', DEFAULT_ERROR)
          ),
        },
      ],
    };
  }
};

export const msPostCentreDocuments = async (_, reqData) => {
  try {
    await centreSerivce().post(CENTRE.postCentreDocuments, {
      centre_documents: (get(reqData, 'uploadedCentreDocuments') || []).map(
        doc => ({
          centre_document_category_id: doc.CentreDocumentCategoryID,
          centre_id: doc.CentreID,
          filename: doc.Filename,
          key: doc.Key,
          remarks: doc.Remarks,
        })
      ),
    });
    return {
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msPostCentreDocuments ',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msSubmitCentreDraft = async (_, reqData) => {
  try {
    const { centreID } = reqData;
    const centreSvc = centreSerivce();
    const resp = await centreSvc.put(CENTRE.submitCentreDraft(centreID));

    return {
      success: true,
      data: {
        submitCentreDraftForSchool: convertObjToCamelCase(
          get(resp, 'data.data', {}),
          ['ID:id']
        ),
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msSubmitCentreDraft',
        fileName,
      },
      'error'
    );
    const fullErrMsg = get(error, 'response.data.error', DEFAULT_ERROR);
    return {
      success: false,
      error: [{ message: fullErrMsg }],
    };
  }
};

export const msFetchCentreManagementConfig = async (_, reqData) => {
  try {
    const categories = {
      workplace: 'workplace_staff',
      govtScheme: 'govt_scheme',
      centreCertification: 'centre_certification',
    };
    const response = await getConfigByCategories(Object.values(categories), {
      schools_ids: [get(reqData, 'schoolID')],
    });
    const getCentreManagementConfig = Object.entries(categories).reduce(
      (acc, [key, value]) => {
        acc[key] = {
          data: (get(response, `${value}.data`) || []).map(item =>
            convertObjToCamelCase(item)
          ),
        };
        return acc;
      },
      {}
    );
    return {
      success: true,
      data: {
        ...getCentreManagementConfig,
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msFetchCentreManagementConfig ',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            error,
            'response.data.message',
            get(error, 'message', DEFAULT_ERROR)
          ),
        },
      ],
    };
  }
};

export const msFetchCentreDocumentCategories = async () => {
  try {
    const resp = await centreSerivce()
      .post(CENTRE.getCentreDocumentCategories, {})
      .then(retrieveListOrObject);

    return {
      success: true,
      data: {
        getCentreDocumentCategoriesConfig: {
          data: (resp || []).map(item => convertObjToCamelCase(item)),
        },
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msFetchCentreDocumentCategories ',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetCentreDocConfig = async (_, reqData) => {
  try {
    const resp = await centreSerivce().post(
      CENTRE.getCentreDocumentUploadURLDocumentCategoryConfig,
      {
        centre_document_category_id: get(reqData, 'centreDocumentCategoryID'),
        centre_id: get(reqData, 'centreID'),
        file: {
          file_path: get(reqData, 'file.filePath'),
          file_size: get(reqData, 'file.fileSize'),
          mime_type: get(reqData, 'file.MIMEType'),
          file_name: get(reqData, 'file.filename'),
        },
      }
    );

    return {
      success: true,
      data: {
        getCentreDocumentUploadURLDocumentCategoryConfig: {
          Url: get(resp, 'data.url', ''),
          Key: get(resp, 'data.key', ''),
        },
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetCentreDocConfig',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msDeleteSchoolCentreDraft = async (_, reqData) => {
  try {
    const resp = await centreSerivce().delete(
      CENTRE.deleteSchoolCentreDraft(get(reqData, 'centreID'))
    );

    return {
      success: !!get(resp, 'data.id'),
      data: {
        deleteCentreDraftForSchool: retrieveObjectMapper(get(resp, 'data'), [
          'ID:id',
        ]),
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msDeleteSchoolCentreDraft',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            error,
            'response.data.error',
            get(error, 'message', DEFAULT_ERROR)
          ),
        },
      ],
    };
  }
};

export const msCancelSchoolCentreDraft = async (_, reqData) => {
  try {
    const resp = await centreSerivce().put(
      CENTRE.cancelSchoolCentreDraft(get(reqData, 'centreID'))
    );

    return {
      success: !!get(resp, 'data.id'),
      data: {
        cancelCentreDraftForSchool: retrieveObjectMapper(get(resp, 'data'), [
          'ID:id',
        ]),
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msCancelSchoolCentreDraft',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            error,
            'response.data.error',
            get(error, 'message', DEFAULT_ERROR)
          ),
        },
      ],
    };
  }
};

export const msFindAllCentreDraftForSchool = async (_, reqData) => {
  try {
    const { IDSchool, pagination = {}, filter = {} } = reqData || {};
    const params = {
      page: get(pagination, 'page', DEFAULT_PAGE),
      perPage: get(pagination, 'perPage', DEFAULT_ITEMS_PER_PAGE),
      ...retrieveSortForMS(get(pagination, 'sort', []), 'sort'),
    };

    const body = {
      school_id: IDSchool,
      filter: convertObjToSnakeCase({ ...filter }),
    };

    const centreSvc = centreSerivce();
    const resp = await centreSvc.post(
      ...CENTRE.getCentreDraftsByFilter(body, params)
    );

    const totalCount = get(resp, 'data.totalCount', 0);
    const drafts = (get(resp, 'data.data', []) || []).map(item =>
      convertObjToCamelCase(item, [], ['status:status_string'])
    );

    const draftIDs = drafts.map(draft => draft.ID);
    const lastUpdatedByUserIds = drafts.map(draft => draft.lastUpdatedByFkUser);
    const [
      centreDraftWorkplaces,
      centreDraftAddresses,
      centreDraftLevels,
      centreDraftNearbyPostcodes,
      centreDraftLevelPrograms,
      linkpointAwardConfigDrafts,
      userList,
    ] = await Promise.all([
      getCentreDraftWorkplaces(draftIDs),
      getCentreDraftAddresses(draftIDs),
      getCentreDraftLevels(draftIDs),
      getCentreDraftNearbyPostcodes(draftIDs),
      getCentreDraftLevelPrograms(draftIDs),
      getLinkpointAwardConfigDraftsByCentreIds(draftIDs),
      getUserByIds(lastUpdatedByUserIds),
    ]);

    const centreDraftWorkplacesByDraftID = groupBy(
      centreDraftWorkplaces,
      'fk_centre_draft'
    );
    const centreDraftAddressesByDraftID = groupBy(
      centreDraftAddresses,
      'fk_centre_draft'
    );
    const centreDraftLevelsByDraftID = groupBy(
      centreDraftLevels,
      'fk_centre_draft'
    );
    const centreDraftNearbyPostcodesByDraftID = groupBy(
      centreDraftNearbyPostcodes,
      'fk_centre_draft'
    );
    const centreDraftLevelProgramsByDraftID = groupBy(
      centreDraftLevelPrograms,
      'fk_centre_draft'
    );
    const linkpointAwardConfigDraftsByDraftID = groupBy(
      linkpointAwardConfigDrafts,
      'fk_centre_draft'
    );
    const userByLastUpdatedByFkUser = keyBy(userList, 'id');

    const buildDraftRelation = (groupedByDraftID, draftId) => {
      const items = get(groupedByDraftID, `${draftId}`, []);
      return {
        totalCount: items.length,
        data: items.map(item => ({
          ...convertObjToCamelCase(item),
        })),
      };
    };

    const a = {
      success: true,
      data: {
        findAllCentreDraftForSchool: {
          totalCount,
          data: drafts.map(draft => ({
            ...convertObjToCamelCase(draft),
            centreDraftWorkplaces: buildDraftRelation(
              centreDraftWorkplacesByDraftID,
              draft?.ID
            ),
            centreDraftAddresses: buildDraftRelation(
              centreDraftAddressesByDraftID,
              draft?.ID
            ),
            centreDraftLevels: buildDraftRelation(
              centreDraftLevelsByDraftID,
              draft?.ID
            ),
            centreDraftNearbyPostcodes: buildDraftRelation(
              centreDraftNearbyPostcodesByDraftID,
              draft?.ID
            ),
            centreDraftLevelPrograms: buildDraftRelation(
              centreDraftLevelProgramsByDraftID,
              draft?.ID
            ),
            linkpointAwardConfigDrafts: buildDraftRelation(
              linkpointAwardConfigDraftsByDraftID,
              draft?.ID
            ),
            userByLastUpdatedByFkUser: retrieveDataMapper(
              userByLastUpdatedByFkUser,
              draft?.lastUpdatedByFkUser,
              ['firstname', 'lastname']
            ),
          })),
        },
      },
    };
    return a;
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msFindAllCentreDraftForSchool',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msCreateOrUpdateSchoolCentreDraft = async (_, reqData) => {
  try {
    const {
      centreData,
      postcodes,
      levels,
      addressData,
      workplaces,
      centreAutoPromotionConfigs,
      centreLevelProgram,
      linkpointConfig,
    } = reqData;

    const payload = {
      ...(addressData && {
        address_data: convertObjToSnakeCase(addressData),
      }),
      centre_draft_update_dto: convertObjToSnakeCase(
        centreData,
        [],
        ['fk_school:fkSchool']
      ),
      ...(levels && { levels }),
      ...(postcodes && { postcodes }),
      ...(workplaces && { workplaces }),
      ...(centreAutoPromotionConfigs && {
        centre_auto_promotion_configs: centreAutoPromotionConfigs.map(config =>
          convertObjToSnakeCase(config)
        ),
      }),
      ...(centreLevelProgram && {
        centre_level_program: centreLevelProgram.map(program =>
          convertObjToSnakeCase(
            program,
            [],
            ['fk_level:fkLevel', 'fk_program:fkProgram']
          )
        ),
      }),
      ...(linkpointConfig && {
        linkpoint_config: convertObjToSnakeCase(linkpointConfig),
      }),
    };

    const centreSvc = centreSerivce();
    const resp = await centreSvc.post(...CENTRE.upsertCentreDraft(payload));

    return {
      success: true,
      data: {
        insertOrUpdateCentreDraftForSchool: convertObjToCamelCase(
          get(resp, 'data', {}),
          ['ID:id']
        ),
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msCreateOrUpdateSchoolCentreDraft',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            error,
            'response.data.error',
            get(error, 'message', DEFAULT_ERROR)
          ),
        },
      ],
    };
  }
};

export const msCreateOrUpdateSchoolCentre = async (_, reqData) => {
  try {
    const {
      fkCentre,
      centreData,
      postcodes,
      levels,
      addressData,
      workplaces,
      centreAutoPromotionConfigs,
      centreLevelProgram,
      linkpointConfig,
    } = reqData;
    const payload = {
      ...(fkCentre && { centre_id: fkCentre }),
      ...(addressData && {
        address_data: convertObjToSnakeCase(addressData),
      }),
      ...(centreData && {
        centre_data: {
          ...convertObjToSnakeCase(
            centreData,
            [],
            ['fk_school:fkSchool', 'fk_govt_scheme:fkGovtScheme']
          ),
        },
      }),
      ...(levels && { levels }),
      ...(postcodes && { postcodes }),
      ...(workplaces && { workplaces }),
      ...(centreAutoPromotionConfigs && {
        centre_auto_promotion_configs: (centreAutoPromotionConfigs || []).map(
          config => convertPascalToSnake(config)
        ),
      }),
      ...(centreLevelProgram && {
        centre_level_program: centreLevelProgram.map(program =>
          convertObjToSnakeCase(
            program,
            [],
            ['fk_centre:fkCentre', 'fk_level:fkLevel', 'fk_program:fkProgram']
          )
        ),
      }),
      ...(linkpointConfig && {
        linkpoint_config: convertObjToSnakeCase(
          linkpointConfig,
          [],
          [
            'merchant_id:merchantID',
            'operator_id:operatorID',
            'terminal_id:terminalID',
          ]
        ),
      }),
    };

    const centreSvc = centreSerivce();
    const resp = await centreSvc.post(...CENTRE.upsertCentre(payload));

    return {
      success: true,
      data: {
        insertOrUpdateCentreForSchool: convertObjToCamelCase(
          get(resp, 'data', {})
        ),
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msCreateOrUpdateSchoolCentre',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            error,
            'response.data.error',
            get(error, 'message', DEFAULT_ERROR)
          ),
        },
      ],
    };
  }
};

export const msGetCapacityDraftsGroupedByCenter = async ({
  fkSchool,
  centreIDs,
}) => {
  try {
    const resp = await centreSerivce().post(
      CENTRE.capacityDraftsGroupedByCenter,
      {
        status: [PENDING],
        centre_ids: centreIDs,
        school_id: fkSchool,
      }
    );
    return {
      capacityDraftsGroupedByCenter: convertObjToCamelCase(
        resp.data,
        [],
        ['CentreID:centreID', 'CentreName:centreName', 'Count:count']
      ),
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'mscapacityDraftsGroupedByCenter',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            error,
            'response.data.error',
            get(error, 'message', DEFAULT_ERROR)
          ),
        },
      ],
    };
  }
};

export const msGetCentreActivityLogs = async (_, reqData) => {
  try {
    const {
      centreID,
      page = DEFAULT_PAGE,
      perPage = DEFAULT_ITEMS_PER_PAGE,
      activeSort = '-updatedAt',
    } = reqData;

    const centreSvc = centreSerivce();
    const response = await centreSvc.post(
      ...CENTRE.getCentreActivityLogs(
        {
          centre_id: centreID,
        },
        {
          page,
          perPage,
          sort: activeSort,
        }
      )
    );

    const logs = response?.data?.data || [];
    const totalCount = response?.data?.totalCount || 0;

    const userIds = uniq(compact(logs.map(log => log.fk_user)));

    let usersMap = {};
    const users = await getUserByIds(userIds);
    usersMap = (users || []).reduce((acc, user) => {
      acc[user.id] = user;
      return acc;
    }, {});

    const transformedData = transformCentreActivityLogs(logs, usersMap);

    return {
      success: true,
      data: {
        getCentreSnapshotByCentreID: {
          totalCount,
          data: transformedData,
        },
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetCentreActivityLogs',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};
